import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Flame } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const WorryShredder = () => {
  const navigate = useNavigate();
  const [worry, setWorry] = useState('');
  const [isDestroying, setIsDestroying] = useState(false);
  const [destroyMode, setDestroyMode] = useState(null); // 'shred' or 'burn'
  const [shreds, setShreds] = useState([]);
  const [flames, setFlames] = useState([]);
  const destroyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (destroyTimeoutRef.current) {
        clearTimeout(destroyTimeoutRef.current);
      }
    };
  }, []);

  const handleShred = () => {
    if (!worry.trim()) return;
    
    setDestroyMode('shred');
    setIsDestroying(true);
    
    // Create shredder strips from the text
    const strips = [];
    const stripCount = 15;
    const textPerStrip = Math.ceil(worry.length / stripCount);
    
    for (let i = 0; i < stripCount; i++) {
      strips.push({
        id: i,
        text: worry.substring(i * textPerStrip, (i + 1) * textPerStrip),
        delay: i * 0.08,
        x: (i - stripCount / 2) * 15
      });
    }
    setShreds(strips);

    if (destroyTimeoutRef.current) {
      clearTimeout(destroyTimeoutRef.current);
    }
    destroyTimeoutRef.current = setTimeout(() => {
      setIsDestroying(false);
      setShreds([]);
      setWorry('');
      setDestroyMode(null);
    }, 3000);
  };

  const handleBurn = () => {
    if (!worry.trim()) return;
    
    setDestroyMode('burn');
    setIsDestroying(true);
    
    // Create flame particles
    const flameParticles = [];
    for (let i = 0; i < 30; i++) {
      flameParticles.push({
        id: i,
        x: Math.random() * 300 - 150,
        delay: Math.random() * 0.5,
        size: Math.random() * 20 + 10
      });
    }
    setFlames(flameParticles);

    if (destroyTimeoutRef.current) {
      clearTimeout(destroyTimeoutRef.current);
    }
    destroyTimeoutRef.current = setTimeout(() => {
      setIsDestroying(false);
      setFlames([]);
      setWorry('');
      setDestroyMode(null);
    }, 4000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8E4F3] to-[#D4CDD4] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-12">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-[#2D3748]">Worry Shredder</h1>
          
          <div className="w-24"></div>
        </header>

        <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-100 relative overflow-hidden min-h-[500px]">
          
          {/* Shredding Animation */}
          <AnimatePresence>
            {isDestroying && destroyMode === 'shred' && (
              <>
                {/* Paper going into shredder */}
                <motion.div
                  initial={{ y: 0, scaleY: 1 }}
                  animate={{ y: 100, scaleY: 0 }}
                  transition={{ duration: 1, ease: 'easeIn' }}
                  className="absolute top-20 left-1/2 transform -translate-x-1/2 w-64 bg-white border-2 border-slate-200 rounded-lg p-4 shadow-lg z-10"
                  style={{ transformOrigin: 'top' }}
                >
                  <p className="text-[#2D3748] text-sm break-words">{worry}</p>
                </motion.div>
                
                {/* Shredder machine */}
                <div className="absolute top-40 left-1/2 transform -translate-x-1/2 w-80 h-16 bg-gradient-to-b from-slate-700 to-slate-800 rounded-lg flex items-center justify-center z-20">
                  <div className="flex gap-1">
                    {[...Array(12)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 0.3, repeat: Infinity, ease: 'linear' }}
                        className="w-3 h-3 bg-slate-500 rounded-full"
                      />
                    ))}
                  </div>
                </div>
                
                {/* Shredded strips falling */}
                {shreds.map(shred => (
                  <motion.div
                    key={shred.id}
                    initial={{ y: 180, x: 0, opacity: 1 }}
                    animate={{ 
                      y: 600, 
                      x: shred.x,
                      opacity: 0,
                      rotate: [0, shred.x > 0 ? 15 : -15]
                    }}
                    transition={{ 
                      duration: 2,
                      delay: shred.delay + 0.8,
                      ease: 'easeIn'
                    }}
                    className="absolute left-1/2 bg-white border border-slate-200 px-1 py-4 rounded-sm text-xs text-[#4A5568] w-6 overflow-hidden"
                    style={{ 
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed'
                    }}
                  >
                    {shred.text}
                  </motion.div>
                ))}
              </>
            )}
          </AnimatePresence>

          {/* Burning Animation */}
          <AnimatePresence>
            {isDestroying && destroyMode === 'burn' && (
              <div className="absolute inset-0 flex items-center justify-center">
                {/* Paper burning */}
                <motion.div
                  initial={{ scale: 1, opacity: 1 }}
                  animate={{ 
                    scale: [1, 1.02, 0.95, 0.8, 0.5, 0],
                    opacity: [1, 1, 0.9, 0.7, 0.3, 0],
                    filter: ['brightness(1)', 'brightness(1.2)', 'brightness(0.8)', 'brightness(0.5)', 'brightness(0.2)', 'brightness(0)']
                  }}
                  transition={{ duration: 3, ease: 'easeIn' }}
                  className="relative w-72 bg-amber-50 border-2 border-amber-200 rounded-lg p-6 shadow-lg"
                  style={{
                    background: 'linear-gradient(180deg, #FFF8E7 0%, #FFE4B5 100%)'
                  }}
                >
                  <p className="text-[#4A4A4A] break-words font-serif">{worry}</p>
                  
                  {/* Burn effect overlay */}
                  <motion.div
                    initial={{ height: '0%' }}
                    animate={{ height: '100%' }}
                    transition={{ duration: 2.5, ease: 'easeIn' }}
                    className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-orange-900 to-transparent rounded-b-lg"
                    style={{ mixBlendMode: 'multiply' }}
                  />
                  
                  {/* Glowing edges */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 3, times: [0, 0.2, 0.8, 1] }}
                    className="absolute inset-0 rounded-lg"
                    style={{
                      boxShadow: 'inset 0 0 30px #FF4500, inset 0 0 60px #FF6347'
                    }}
                  />
                </motion.div>
                
                {/* Flame particles */}
                {flames.map(flame => (
                  <motion.div
                    key={flame.id}
                    initial={{ y: 0, opacity: 0, scale: 0 }}
                    animate={{ 
                      y: -200,
                      opacity: [0, 1, 1, 0],
                      scale: [0.5, 1, 0.8, 0]
                    }}
                    transition={{ 
                      duration: 1.5,
                      delay: flame.delay + 0.5,
                      repeat: 2,
                      repeatDelay: 0.3
                    }}
                    className="absolute"
                    style={{ 
                      left: `calc(50% + ${flame.x}px)`,
                      bottom: '30%'
                    }}
                  >
                    <div 
                      className="rounded-full blur-sm"
                      style={{
                        width: flame.size,
                        height: flame.size * 1.5,
                        background: `radial-gradient(ellipse at bottom, #FF4500 0%, #FF6347 30%, #FFD700 60%, transparent 100%)`
                      }}
                    />
                  </motion.div>
                ))}
                
                {/* Smoke */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.3, 0.5, 0.3, 0] }}
                  transition={{ duration: 3, delay: 1 }}
                  className="absolute top-10 left-1/2 transform -translate-x-1/2"
                >
                  <div className="w-32 h-32 bg-slate-400 rounded-full blur-2xl opacity-50" />
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Input Form */}
          {!isDestroying && (
            <div>
              <label className="block text-lg font-semibold text-[#2D3748] mb-4">
                What's worrying you today?
              </label>
              
              <textarea
                data-testid="worry-input"
                value={worry}
                onChange={(e) => setWorry(e.target.value)}
                placeholder="Type your worry here... Watch it disappear forever."
                className="w-full h-48 p-6 rounded-2xl border-2 border-slate-200 bg-slate-50 focus:ring-2 focus:ring-[#81B29A]/20 focus:border-[#81B29A] outline-none transition-all text-lg resize-none"
                maxLength={300}
              />
              
              <div className="flex justify-between items-center mt-6">
                <span className="text-sm text-[#4A5568]">
                  {worry.length}/300 characters
                </span>
                
                <div className="flex gap-3">
                  <button
                    data-testid="shred-btn"
                    onClick={handleShred}
                    disabled={!worry.trim()}
                    className="bg-gradient-to-r from-slate-600 to-slate-700 text-white px-6 py-4 rounded-full font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center gap-2"
                  >
                    <Trash2 className="w-5 h-5" />
                    Shred It
                  </button>
                  
                  <button
                    data-testid="burn-btn"
                    onClick={handleBurn}
                    disabled={!worry.trim()}
                    className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-4 rounded-full font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center gap-2"
                  >
                    <Flame className="w-5 h-5" />
                    Burn It
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Processing message */}
          {isDestroying && (
            <div className="absolute bottom-8 left-0 right-0 text-center">
              <p className="text-xl font-bold text-[#81B29A]">
                {destroyMode === 'shred' ? '🗑️ Shredding your worries...' : '🔥 Burning it away...'}
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 bg-[#E0F7FA] rounded-2xl p-6 text-center">
          <p className="text-[#4A5568]">
            Write down what's bothering you, then choose to shred it or burn it. 
            Sometimes letting go is as simple as watching it disappear.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorryShredder;
